import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SecuritySettings } from './security-settings/security-settings';
import { AppearanceSettingsComponent } from './appearance-settings/appearance-settings';
import { NotificationsSettings } from './notifications-settings/notifications-settings';
import { HoursSettings } from './hours-settings/hours-settings';
import { BillingSettings } from './billing-settings/billing-settings';
import { PricingSettings } from './pricing-settings/pricing-settings';
import { SubscriptionSettings } from './subscription-settings/subscription-settings';
import { HasRolePipe } from '../../core/pipes/has-role.pipe';
import { User } from '../../core/models/user.model';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { ClinicStatusService } from '../../core/services/clinic-status.service';
import { UserManagementService } from '../../core/services/user-management.service';
import { ClinicMembership } from '../../core/models/clinic-membership.model';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';

@Component({
  selector: 'app-settings',
  imports: [
    SecuritySettings,
    HasRolePipe,
    AppearanceSettingsComponent,
    NotificationsSettings,
    HoursSettings,
    BillingSettings,
    PricingSettings,
    SubscriptionSettings,
    PortalNavigation,
  ],
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
})
export class Settings implements OnInit {
  activeTab = 'security';
  userProfile: User | null = null;
  activeRole = 'DOCTOR';
  isClinicActive = false;
  clinicMemberships: ClinicMembership[] = [];
  readonly clinicNavigationItems: PortalNavigationItem[] = [
    { label: 'Patients', icon: 'bi-people-fill', route: '/dashboard/patients-view' },
    { label: 'Bookings', icon: 'bi-calendar-check-fill', route: '/dashboard/booking-list' },
    { label: 'Create Booking', icon: 'bi-calendar-plus-fill', route: '/dashboard/create-booking' },
  ];
  readonly managerNavigationItems: PortalNavigationItem[] = [
    { label: 'Reporting', icon: 'bi-bar-chart-line', route: '/dashboard/reporting' },
    { label: 'User Management', icon: 'bi-people', route: '/dashboard/manage-users' },
  ];

  constructor(
    private clinicStatusService: ClinicStatusService,
    private userManagementService: UserManagementService,
    private profileService: DoctorProfileService,
    private route: ActivatedRoute,
  ) { }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  ngOnInit(): void {
    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam) {
      this.activeTab = tabParam;
    } else {
      this.activeTab = 'security';
    }
    this.route.queryParams.subscribe((params) => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
    });
    this.loadUserProfile();
    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.userManagementService.getClinicMemberships().subscribe({
      next: (memberships) => {
        this.clinicMemberships = memberships;
      },
      error: () => {
        this.clinicMemberships = [];
      },
    });
  }

  onSwitchClinic(clinicId: string): void {
    this.profileService.switchClinicContext(clinicId);
  }

  getPermissionLevel(): string {
    const role = localStorage.getItem('userRole');
    return role ? role : 'UNKNOWN';
  }

  get portalName(): string {
    return `${this.activeRole.charAt(0)}${this.activeRole.slice(1).toLowerCase()} Portal`;
  }

  get portalIcon(): string {
    return this.activeRole === 'MANAGER'
      ? 'bi-clipboard2-pulse-fill'
      : this.activeRole === 'RECEPTION'
        ? 'bi-hospital-fill'
        : 'bi-heart-pulse-fill';
  }

  get portalClass(): string {
    return this.activeRole === 'MANAGER' ? 'text-danger' : 'text-primary';
  }

  get profileRoute(): string {
    return '/dashboard/profile';
  }

  get primaryNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? [] : this.clinicNavigationItems;
  }

  get additionalNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? this.managerNavigationItems : [];
  }

  private loadUserProfile(): void {
    const storedProfile = localStorage.getItem('userProfile');
    this.activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';

    if (!storedProfile) {
      return;
    }

    try {
      this.userProfile = JSON.parse(storedProfile) as User;
    } catch {
      this.userProfile = null;
    }
  }
}
